import React, { Suspense, useEffect } from "react";
import { IconButtonV2 } from "src/components/IconButtonV2/IconButtonV2.tsx";
import { ButtonV2 } from "src/components/ButtonV2/ButtonV2.tsx";

import { useGlobalSettings } from "src/hooks/use-global-settings.ts";
import { useForm } from "src/lib/react-hook-form/useForm.tsx";
import type { StorageKey } from "@exonn/storage";
import { FormProvider } from "react-hook-form";
import { LoadingOverlay } from "src/components/LoadingOverlay/LoadingOverlay.tsx";
import { useMutation } from "@tanstack/react-query";
import { getExonnApi } from "src/exonn-api/exonn-api.tsx";
import { Collapse } from "src/admin/administration/components/Collapse/Collapse.tsx";

import { useStorageState } from "src/hooks/use-storage-state.ts";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";
import { removeItem } from "@exonn/utils";
import { GlobalSettings } from "src/admin/administration/tabs/SystemTab/groups/GlobalSettings.tsx";
import { ProductSettings } from "src/admin/administration/tabs/SystemTab/groups/ProductSettings.tsx";
import type { SystemSettingsFormValues } from "src/admin/administration/tabs/SystemTab/types.ts";
import { TaxAndBookingSettings } from "src/admin/administration/tabs/SystemTab/groups/TaxAndBookingSettings.tsx";
import { SupplierSettings } from "src/admin/administration/tabs/SystemTab/groups/SupplierSettings.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useSnackbar } from "src/App/App.hooks.ts";
import * as styles from "./SystemTab.style.ts";
import { FormV2 } from "src/components/FormV2/FormV2.tsx";
import { useRestoreScrollPosition } from "src/hooks/use-restore-scroll-position.ts";
import axios from "axios";

const storageKey: StorageKey = {
  name: "global-settings-form",
  version: "1.0.0",
};
const defaultValues: SystemSettingsFormValues = {
  default_country_id: "",
  default_language_id: "",
  default_currency_id: "",
  prices_defined_in_net: false,
  prices_show_in_net: false,
  delivery_methods_that_require_tax_calculation: [],
  countries_vat_status: [],
  use_commission_supplier_orders: false,
};
enum SectionsEnum {
  Global = "global",
  Product = "product",
  Tax = "tax",
  Supplier = "supplier",
}
const SystemTab = () => {
  return (
    <Suspense fallback={null}>
      <SystemViewImpl />
    </Suspense>
  );
};
const collapsedStorageKey = {
  name: "global-settings-collapsed",
  version: "1.0.0",
};
let scrollPosition = 0;
const SystemViewImpl = () => {
  const snackbar = useSnackbar();
  const [collapsed, setCollapsed] = useStorageState<string[]>(
    collapsedStorageKey,
    [
      SectionsEnum.Global,
      SectionsEnum.Product,
      SectionsEnum.Tax,
      SectionsEnum.Supplier,
    ],
    memoryStorageDriver,
  );

  const toggleSection = (id: string) => (collapsed: boolean) => {
    setCollapsed((prev) =>
      collapsed ? [...removeItem(prev, id), id] : removeItem(prev, id),
    );
  };

  const {
    data: globalSettingsData,
    refetch: refetchGlobalSettings,
    isFetching,
  } = useGlobalSettings({
    structuralSharing: false,
  });

  const { mutate: updateSettings, isPending: isUpdatingSettings } = useMutation(
    {
      mutationFn: async (values: SystemSettingsFormValues) => {
        const api = getExonnApi();
        const response = await api.system.globalSettings.update({}, values);
        return response.data;
      },
      // onSettled: () => {
      //   queryClient.invalidateQueries({
      //     queryKey: queries.all({
      //       resource: ResourceEnum.Country,
      //     }),
      //     type: "all",
      //   });
      //   queryClient.invalidateQueries({
      //     queryKey: queries.all({
      //       resource: ResourceEnum.Language,
      //     }),
      //     type: "all",
      //   });
      //
      //   refetchGlobalSettings();
      // },
    },
  );

  const { form, getValuesFromStorage, clearStorage } =
    useForm<SystemSettingsFormValues>({
      storageKey,
      defaultValues: defaultValues,
    });

  const { t } = useTranslation(TranslationNamespaceEnum.Administration);

  useEffect(() => {
    const persistedValues = getValuesFromStorage();
    if (globalSettingsData && !persistedValues) {
      form.reset({
        ...defaultValues,
        default_country_id: globalSettingsData.default_country.id,
        default_language_id: globalSettingsData.default_language.id,
        default_currency_id: globalSettingsData.default_currency.id,
        prices_defined_in_net: globalSettingsData.prices_defined_in_net,
        prices_show_in_net: globalSettingsData.prices_show_in_net,
        use_commission_supplier_orders:
          globalSettingsData.use_commission_supplier_orders,
        delivery_methods_that_require_tax_calculation:
          globalSettingsData.delivery_methods_that_require_tax_calculation,
        countries_vat_status: globalSettingsData.countries_vat_status,
      });
    } else {
      form.reset({
        ...defaultValues,
        ...persistedValues,
      });
    }
  }, [globalSettingsData, form, getValuesFromStorage]);

  const handleRefresh = () => {
    clearStorage();
    refetchGlobalSettings();
  };

  const handleSubmit = (values: SystemSettingsFormValues) => {
    updateSettings(values, {
      onSuccess: () => {
        snackbar.success({
          flash: true,
          message: t("system.form.notifications.success"),
        });
        refetchGlobalSettings();
      },
      onError: (e) => {
        if (axios.isAxiosError(e)) {
          const errors = e.response?.data.errors;
          Object.keys(errors || {}).forEach((key) => {
            form.setError(`root.${key}`, {
              message: errors[key][0],
            });
          });
        }
        snackbar.error({
          flash: true,
          message: t("system.form.notifications.failure"),
        });
      },
    });
  };

  const { containerRef: connectScrollContainerRef } = useRestoreScrollPosition({
    onSavePosition: ({ top }) => {
      scrollPosition = top;
    },
    getSavedPosition: () => ({
      top: scrollPosition,
    }),
  });

  return (
    <FormV2.Root onSubmit={form.handleSubmit(handleSubmit)}>
      <LoadingOverlay.Root isLoading={isFetching || isUpdatingSettings}>
        <LoadingOverlay.Spinner />
        <LoadingOverlay.Title>
          {t("advices.pleaseWait", {
            ns: TranslationNamespaceEnum.Common,
          })}
        </LoadingOverlay.Title>
      </LoadingOverlay.Root>
      <FormV2.Inner ref={connectScrollContainerRef}>
        <FormV2.Toolbar>
          <IconButtonV2
            onClick={handleRefresh}
            size={"sm"}
            skin={"info"}
            variant={"outlined"}
          >
            <i className={"icon-15-update"} />
          </IconButtonV2>
          <ButtonV2
            startAdornment={<span className={"icon-15-save"} />}
            size={"sm"}
            type={"submit"}
            skin={"success"}
            variant={"filled"}
          >
            {t("actions.save", {
              ns: TranslationNamespaceEnum.Common,
            })}
          </ButtonV2>
        </FormV2.Toolbar>
        <div css={styles.content}>
          <FormProvider {...form}>
            <Collapse
              icon={<span className={"icon-15-settings"} />}
              onCollapsedChange={toggleSection(SectionsEnum.Global)}
              collapsed={collapsed.includes(SectionsEnum.Global)}
              title={t("system.form.groups.general")}
            >
              <GlobalSettings />
            </Collapse>
            <Collapse
              icon={<span className={"icon-15-article"} />}
              onCollapsedChange={toggleSection(SectionsEnum.Product)}
              collapsed={collapsed.includes(SectionsEnum.Product)}
              title={t("system.form.groups.product")}
            >
              <ProductSettings />
            </Collapse>
            <Collapse
              icon={<span className={"icon-15-tax"} />}
              onCollapsedChange={toggleSection(SectionsEnum.Tax)}
              collapsed={collapsed.includes(SectionsEnum.Tax)}
              title={t("system.form.groups.taxAndBooking")}
            >
              <TaxAndBookingSettings />
            </Collapse>
            <Collapse
              icon={<span className={"icon-15-suppliers"} />}
              onCollapsedChange={toggleSection(SectionsEnum.Supplier)}
              collapsed={collapsed.includes(SectionsEnum.Supplier)}
              title={t("system.form.groups.supplier")}
            >
              <SupplierSettings />
            </Collapse>
          </FormProvider>
        </div>
      </FormV2.Inner>
    </FormV2.Root>
  );
};

export default SystemTab;
